import { FC, useMemo } from 'react'
import { replace, RouteObject, useSubmit } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import Persona from 'persona-react'
import './kyc.css'

// COMPONENTS
import { Column } from '@components/templates/column'

// TYPES
import type { Fields, FieldValue, InquiryOptions } from 'persona/dist/lib/interfaces'

// UTILITIES
import { PersonaAPI } from '@apis'
import { parseKycStatus, parseTeraUserData } from '@schemas'
import { createKycVerifiedLoader, useLoader } from '@utils/loaders'
import { createAction } from '@utils/actions'


interface Props { }

const KycStack: FC<Props> = () => {
    const { isLoaded, isSignedIn, user } = useUser()
    const { id: inquiryId, sessionToken } = useLoader<typeof loader>()
    const submit = useSubmit()

    const fields = useMemo<Fields>(() => {
        const obj: Record<string, FieldValue | null | undefined> = {
            nameFirst: user?.firstName,
            nameLast: user?.lastName,
            birthdate: undefined, // "2000-12-31"
            addressStreet1: undefined, // "132 Second St."
            addressCity: undefined, // "San Francisco"
            addressSubdivision: undefined, // "California"
            addressPostalCode: undefined, // "93441"
            addressCountryCode: undefined, // "US"
            phoneNumber: user?.primaryPhoneNumber?.phoneNumber,
            emailAddress: user?.primaryEmailAddress?.emailAddress,
            customAttribute: undefined, // "hello"
        }
        return Object.keys(obj).reduce<Fields>((prev, key) => (obj[key] != null ? { ...prev, [key]: obj[key] } : prev), {})
    }, [user?.firstName, user?.lastName])

    const handleCompleteEvent: InquiryOptions['onComplete'] = (args) => {
        logEvent("Complete", args)
        const formData = new FormData()
        formData.set('inquiryId', inquiryId)
        formData.set('status', args.status)
        submit(formData, { method: 'POST' })
    }

    function logEvent(event: string, args?: Record<any, any>) {
        console.log(`Persona ${event} Event:`, args)
    }

    return (
        <Column.Content>
            <div className="persona-verification">
                {(isLoaded && isSignedIn && !!user.id && !!inquiryId && !!sessionToken) && (
                    <Persona referenceId={user.id}
                        // templateId={import.meta.env.VITE_PERSONA_TEMPLATE_ID}
                        inquiryId={inquiryId}
                        sessionToken={sessionToken}
                        environmentId={import.meta.env.VITE_PERSONA_ENVIRONMENT_ID}
                        fields={fields}
                        onCancel={(args) => logEvent("Cancel", args)}
                        // Inquiry complete. This callback is only called when the user presses the complete button, so it's not reliable.
                        onComplete={handleCompleteEvent}
                        onError={(args) => logEvent("Error", args)}
                        // onEvent={(eventName, metadata) => logEvent("Event", { eventName, metadata })}
                        onLoad={() => logEvent("Load")}
                        onReady={() => logEvent("Ready")}
                    />
                )}
            </div>
        </Column.Content>
    )
}
const loader = createKycVerifiedLoader(async ({ request }, isKycVerified, isNewUser) => {
    const tera = parseTeraUserData(window.Clerk?.user?.publicMetadata.tera)
    if (!tera.address) return replace('../billing')

    if (isKycVerified || !isNewUser) return replace("/account");
    const inquiry = await PersonaAPI.CreateInquiryRequest.fetch(window.Clerk?.user?.id)

    if (inquiry.status === 'approved' || inquiry.status === 'completed') return completeKyc(inquiry.id, inquiry.status, request)
    else return inquiry
})

const action = createAction(async ({ request }) => {
    const formData = await request.formData()
    const inquiryId = `${formData.get('inquiryId')}`
    const status = `${formData.get('status')}`
    return completeKyc(inquiryId, status, request)
})

type status = "approved" | "completed" | "created" | "pending" | "declined" | "needs_review" | "expired" | "failed" | undefined
const completeKyc = (inquiryId: string, status: string, request: Request) => {
    const redirect = new URL(request.url).searchParams.get('from')
    const user = window.Clerk?.user

    if (!!user && inquiryId) {
        const timestamp = new Date().toISOString().split('.')[0] + 'Z' // Split to remove the milliseconds in the string
        const publicMetaData: UserPublicMetadata = {
            tera: parseTeraUserData(user?.publicMetadata?.tera),
            kyc: parseKycStatus({
                ...(user?.publicMetadata?.kyc || {}),
                id: inquiryId,
                status: status as status,
                updatedAt: timestamp,
                completedAt: timestamp,
            }),
        }
        user.publicMetadata = publicMetaData
    }

    // Check if redirect URL matches allowed patterns
    if (redirect) {
        const allowedPatterns = [
            /^\/group\/[^\/]+\/pledge\/review$/,    // /group/{groupId}/pledge/review
            /^\/group\/[^\/]+\/add\/[^\/]+$/,       // /group/{groupId}/add/{pledgeId}
        ]

        const isAllowedRedirect = allowedPatterns.some(pattern => pattern.test(redirect))
        if (isAllowedRedirect) {
            return replace(redirect)
        }
    }

    return replace('../kyc-explainer')
}

const KycRoute: RouteObject = {
    path: "kyc",
    element: <KycStack />,
    loader,
    action,
}

export default KycRoute
